import { useMemo } from "react";
import { useTheme } from "styled-components";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import * as countryCodes from "country-codes-list";
import {
  Button,
  Combobox,
  ComboboxItem,
  TextField,
  TextFieldErrorMessage,
} from "@gds-react/core";
import { useAccount } from "../../../../hooks/useAccount";
import { useAccountMutations } from "../../../../hooks/useAccountMutations";
import {
  PatchAccountRequest,
  ProblemDetails,
} from "../../../../generated-api/account";
import { Spinner } from "../../../LoadingPage/LoadingPage.styles";
import ButtonSpinner from "../../../../components/Spinner";
import Toast from "../../../../components/Toast";
import { GlobalTheme } from "../../../../types/GlobalTheme";
import {
  ButtonWrapper,
  LoaderWrapper,
  Row,
  RowLabel,
  Rows,
  Section,
  SectionDescription,
  SectionHeader,
  SectionTitle,
  Wrapper,
} from "./AccountInformationTab.styles";

interface Props {
  accountId?: string;
}

interface FormValues {
  name: string;
  cvrVat: string;
  accountResponsibleContactName: string;
  accountResponsibleContactNumber: string;
  accountResponsibleEmailAddress: string;
  street: string;
  postalCode: string;
  city: string;
  state: string;
  countryIsoCode: string;
}

export default function AccountInformationTab({ accountId }: Props) {
  const { t, i18n } = useTranslation();
  const theme = useTheme() as GlobalTheme;
  const { data, isLoading } = useAccount(accountId, true);
  const { patch } = useAccountMutations();

  const countriesOptions = useMemo(() => countryCodes.all(), []);

  const initialValues = useMemo((): FormValues => {
    const countryIsoCode = data?.model.country
      ? countriesOptions.find(
          (c) =>
            c.countryNameEn.toLowerCase() === data.model.country.toLowerCase(),
        )?.countryCode
      : "";

    return {
      name: data?.model.name ?? "",
      cvrVat: data?.model.vatCode ?? "",
      accountResponsibleContactName: data?.model.contactPerson ?? "",
      accountResponsibleContactNumber: data?.model.phoneNumber ?? "",
      accountResponsibleEmailAddress: data?.model.email ?? "",
      street: data?.model.street ?? "",
      postalCode: data?.model.postalCode ?? "",
      city: data?.model.city ?? "",
      state: data?.model.state ?? "",
      countryIsoCode: countryIsoCode ?? "",
    };
  }, [data, countriesOptions]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .min(3, t("accountPage.tabs.accountInformation.form.name.minLength"))
          .max(
            100,
            t("accountPage.tabs.accountInformation.form.name.maxLength"),
          )
          .required(
            t("accountPage.tabs.accountInformation.form.name.required"),
          ),
        accountResponsibleEmailAddress: yup
          .string()
          .email(
            t(
              "accountPage.tabs.accountInformation.form.accountResponsibleEmailAddress.invalid",
            ),
          ),
      }),
    [t],
  );

  const submit = async (values: FormValues) => {
    if (!accountId) {
      return;
    }

    try {
      await patch.mutateAsync({
        accountId,
        patchAccountRequest: values as PatchAccountRequest,
      });

      toast.success(
        <Toast
          variant="success"
          title={t(
            "accountPage.tabs.accountInformation.form.saveSuccess.title",
          )}
          description={t(
            "accountPage.tabs.accountInformation.form.saveSuccess.description",
          )}
        />,
        { icon: false },
      );
    } catch (err) {
      const error = err as ProblemDetails;

      const hasTitleTranslation = Boolean(
        error.titleLocalizationKey && i18n.exists(error.titleLocalizationKey),
      );
      const hasDescriptionTranslation = Boolean(
        error.detailLocalizationKey && i18n.exists(error.detailLocalizationKey),
      );

      toast.error(
        <Toast
          variant="error"
          title={
            hasTitleTranslation
              ? t(error.titleLocalizationKey as string)
              : error.title
                ? error.title
                : t("accountPage.tabs.accountInformation.form.saveError.title")
          }
          description={
            hasDescriptionTranslation
              ? t(error.detailLocalizationKey as string)
              : error.detail
                ? error.detail
                : t(
                    "accountPage.tabs.accountInformation.form.saveError.description",
                  )
          }
        />,
        { icon: false },
      );
    }
  };

  const {
    values,
    errors,
    touched,
    dirty,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
  } = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit: submit,
    enableReinitialize: true,
  });

  if (isLoading) {
    return (
      <LoaderWrapper>
        <Spinner />
      </LoaderWrapper>
    );
  }

  return (
    <Wrapper as="form" onSubmit={handleSubmit}>
      <Section>
        <SectionHeader>
          <SectionTitle>
            {t(
              "accountPage.tabs.accountInformation.sections.generalInformation.title",
            )}
          </SectionTitle>
          <SectionDescription>
            {t(
              "accountPage.tabs.accountInformation.sections.generalInformation.description",
            )}
          </SectionDescription>
        </SectionHeader>
        <Rows>
          <Row>
            <RowLabel>
              {t("accountPage.tabs.accountInformation.form.name.label")}
            </RowLabel>
            <TextField
              name="name"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={Boolean(touched.name && errors.name)}
              errorMessage={
                touched.name && errors.name ? (
                  <TextFieldErrorMessage>{errors.name}</TextFieldErrorMessage>
                ) : undefined
              }
            />
          </Row>
          <Row>
            <RowLabel>
              {t("accountPage.tabs.accountInformation.form.cvrVat.label")}
            </RowLabel>
            <TextField
              name="cvrVat"
              value={values.cvrVat}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Row>
        </Rows>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>
            {t(
              "accountPage.tabs.accountInformation.sections.accountResponsible.title",
            )}
          </SectionTitle>
          <SectionDescription>
            {t(
              "accountPage.tabs.accountInformation.sections.accountResponsible.description",
            )}
          </SectionDescription>
        </SectionHeader>
        <Rows>
          <Row>
            <RowLabel>
              {t(
                "accountPage.tabs.accountInformation.form.accountResponsibleContactName.label",
              )}
            </RowLabel>
            <TextField
              name="accountResponsibleContactName"
              value={values.accountResponsibleContactName}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Row>
          <Row>
            <RowLabel>
              {t(
                "accountPage.tabs.accountInformation.form.accountResponsibleContactNumber.label",
              )}
            </RowLabel>
            <TextField
              name="accountResponsibleContactNumber"
              value={values.accountResponsibleContactNumber}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Row>
          <Row>
            <RowLabel>
              {t(
                "accountPage.tabs.accountInformation.form.accountResponsibleEmailAddress.label",
              )}
            </RowLabel>
            <TextField
              type="email"
              name="accountResponsibleEmailAddress"
              value={values.accountResponsibleEmailAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              invalid={Boolean(
                touched.accountResponsibleEmailAddress &&
                errors.accountResponsibleEmailAddress,
              )}
              errorMessage={
                touched.accountResponsibleEmailAddress &&
                errors.accountResponsibleEmailAddress ? (
                  <TextFieldErrorMessage>
                    {errors.accountResponsibleEmailAddress}
                  </TextFieldErrorMessage>
                ) : undefined
              }
            />
          </Row>
        </Rows>
      </Section>

      <Section>
        <SectionHeader>
          <SectionTitle>
            {t(
              "accountPage.tabs.accountInformation.sections.companyAddress.title",
            )}
          </SectionTitle>
          <SectionDescription>
            {t(
              "accountPage.tabs.accountInformation.sections.companyAddress.description",
            )}
          </SectionDescription>
        </SectionHeader>
        <Rows>
          <Row>
            <RowLabel>
              {t("accountPage.tabs.accountInformation.form.street.label")}
            </RowLabel>
            <TextField
              name="street"
              value={values.street}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Row>
          <Row>
            <RowLabel>
              {t("accountPage.tabs.accountInformation.form.postalCode.label")}
            </RowLabel>
            <TextField
              name="postalCode"
              value={values.postalCode}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Row>
          <Row>
            <RowLabel>
              {t("accountPage.tabs.accountInformation.form.city.label")}
            </RowLabel>
            <TextField
              name="city"
              value={values.city}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Row>
          <Row>
            <RowLabel>
              {t("accountPage.tabs.accountInformation.form.state.label")}
            </RowLabel>
            <TextField
              name="state"
              value={values.state}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Row>
          <Row>
            <RowLabel>
              {t("accountPage.tabs.accountInformation.form.country.label")}
            </RowLabel>
            <Combobox
              placeholder={t(
                "accountPage.tabs.accountInformation.form.country.placeholder",
              )}
              value={values.countryIsoCode}
              onValueChange={(value) => setFieldValue("countryIsoCode", value)}
            >
              {countriesOptions.map((country) => (
                <ComboboxItem
                  key={country.countryCode}
                  value={country.countryCode}
                >
                  {country.countryNameEn}
                </ComboboxItem>
              ))}
            </Combobox>
          </Row>
        </Rows>
      </Section>

      <ButtonWrapper>
        <Button
          type="submit"
          disabled={!dirty || patch.isPending}
          style={{ minWidth: 124 }}
        >
          {patch.isPending ? (
            <ButtonSpinner
              size="small"
              color={theme.colors.button.primary.color}
            />
          ) : (
            t("accountPage.tabs.accountInformation.form.buttonUpdateText")
          )}
        </Button>
      </ButtonWrapper>
    </Wrapper>
  );
}
